import React, { useLayoutEffect, useRef, useState } from 'react'
import { motion, useAnimationControls } from 'framer-motion'
import { heightMotion } from '@/src/renderer/lib/motion/heightMotion'

/** Die Höhe einer Zeile, wie `SubtaskList.scss` sie setzt — das Maß der Bewegung. */
export const ROW_HEIGHT = 34

const grow = heightMotion({ height: ROW_HEIGHT })

interface MotionRowProps {
  /** Schaltet die Bewegung ab (`prefers-reduced-motion`). */
  reduced: boolean
  /**
   * `false` für eine Zeile, die **schon dasteht**, wenn sie zum ersten Mal
   * gerendert wird.
   *
   * Eine frisch abgeschickte Unteraufgabe tritt an die Stelle der **offenen
   * Zeile** — dort stand ihr Text die ganze Zeit. Fährt sie trotzdem von Höhe 0
   * auf, ist sie für den Anlauf unsichtbar, und das Getippte scheint kurz weg zu
   * sein (gemeldet 2026-09-20).
   */
  appear?: boolean
  /**
   * Spielt die Einfahrt erneut ab, sooft sich dieser Wert ändert.
   *
   * Für die **offene Zeile**: Sie behält über alle Plätze denselben Schlüssel —
   * sie wandert, statt neu zu entstehen, und rettet so ihren getippten Text.
   * Damit gibt es aber auch kein Ankommen, das Framer von sich aus animieren
   * würde. Wer sie mit Cmd/Ctrl+Enter in die Mitte holt, soll sie trotzdem
   * aufgehen sehen.
   */
  replay?: string | null
  children: React.ReactNode
}

/**
 * Der Kasten, in dem eine Checklisten-Zeile **Platz macht** und wieder
 * verschwindet: Höhe auf, Inhalt ein — und rückwärts beim Gehen
 * (`lib/motion/heightMotion`).
 *
 * **Senkrecht und sonst nichts** (Nutzer-Vorgabe, 2026-09-20). Zwei Dinge
 * mussten dafür weg:
 *
 * - **`layout`.** Framers Layout-Animation zieht ein Element von seiner vorigen
 *   Stelle an die neue — und beim Öffnen der **Aufgaben-Details** wandert die
 *   ganze Liste quer über die Karte, also fuhren die Zeilen von rechts herein.
 *   Das Nachrücken der Nachbarn braucht sie ohnehin nicht: Es entsteht von
 *   selbst, weil dieser Kasten höher oder flacher wird.
 * - **Die Skalierung.** `growMotion` (der erste Anlauf) fährt zusätzlich von
 *   0,7 auf 1. Auf einer schmalen Zeile ist das kaum zu sehen, auf einer breiten
 *   zieht es den Inhalt zur Mitte zusammen — und genau das las sich als
 *   Hereinfliegen von der Seite. `heightMotion` kennt nur Höhe und Deckkraft.
 *
 * **`overflow` nur während der Bewegung** (gemeldet 2026-09-20): Es hält den
 * Inhalt im Kasten, solange der noch flach ist. Bliebe es stehen, schnitte es
 * ab, was aus einer Zeile herausragt — der 3px-Ring der offenen Zeile, der
 * Schatten einer gezogenen.
 */
export const MotionRow: React.FC<MotionRowProps> = ({ reduced, appear = true, replay, children }) => {
  /*
    **Startet auf `false`, nicht auf `true`.** `onAnimationStart` feuert, bevor
    das erste Bild steht — das genügt. Andersherum bliebe der Kasten für immer
    zugeschnitten, wenn Framer gar nicht erst anläuft (`AnimatePresence
    initial={false}` beim Aufbau der Liste) und deshalb auch nichts abschließt.
    Genau so sah der gemeldete Fehler aus: die offene Zeile ohne ihren Ring.
  */
  const [moving, setMoving] = useState(false)

  const controls = useAnimationControls()
  const erstesBild = useRef(true)
  /*
    **Vor dem Bild, nicht danach** (gemeldet 2026-09-20). Ein `useEffect` läuft
    erst, wenn gezeichnet ist: Die Zeile stand dann ein Bild lang in voller Höhe
    an ihrem neuen Platz, verschwand im nächsten (weil der Anfangszustand sie auf
    0 setzt) und fuhr erst danach auf — dreimal Bewegung für einen Vorgang.

    Gemessen wird hier nichts, nur gesetzt; die TabStack-Falle
    (`display: none` ⇒ alles 0) greift also nicht.
  */
  useLayoutEffect(() => {
    if (reduced) return
    /*
      Beim Aufbau nur hinstellen: Eine bestehende Liste fährt nicht ein.

      Der Schutz hängt am **Mounten**, nicht am ersten Lauf mit `replay` — die
      Liste gibt den Wert erst, wenn die offene Zeile in die Mitte geht. Läge er
      dort, verschluckte er genau die erste Einfahrt.
    */
    if (erstesBild.current) {
      erstesBild.current = false
      if (replay !== undefined) controls.set(grow.animate)
      return
    }
    if (replay === undefined) return
    controls.set(grow.initial)
    void controls.start(grow.animate)
  }, [replay, reduced, controls])

  if (reduced) return <div style={{ flexShrink: 0 }}>{children}</div>

  return (
    <motion.div
      initial={appear ? grow.initial : false}
      animate={replay === undefined ? grow.animate : controls}
      exit={grow.exit}
      onAnimationStart={() => setMoving(true)}
      onAnimationComplete={() => setMoving(false)}
      style={{ overflow: moving ? 'hidden' : 'visible', flexShrink: 0 }}
    >
      {children}
    </motion.div>
  )
}
